import type { ReactNode } from "react";
import { FileText, FolderPlus } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/**
 * One of the Folders view's create actions. The header buttons and the
 * background menu both render from the same descriptor, so the two can never
 * disagree about when an action is allowed.
 */
export interface FolderViewCreateAction {
  run: () => void;
  /** Why the action is unavailable here; `undefined` when it is available. */
  blockedReason: string | undefined;
}

interface FolderViewBackgroundMenuProps {
  /** The single element the menu attaches to (the view's content column). */
  children: ReactNode;
  /** No menu at all: the native one passes through. */
  disabled?: boolean;
  newFolder: FolderViewCreateAction;
  /** Omitted where the page offers no New document. */
  newDocument?: FolderViewCreateAction | null;
}

/**
 * Right-click on the Folders view's empty space — anywhere that is not a
 * document or folder row — offers New folder and New document in the open
 * folder. Rows keep their own menus: an enabled row menu prevents the event's
 * default, which Radix reads as "handled" and so never opens this one, and a
 * disabled row stops the event before it reaches here (DocumentRowContextMenu).
 */
export function FolderViewBackgroundMenu({
  children,
  disabled = false,
  newFolder,
  newDocument,
}: FolderViewBackgroundMenuProps) {
  const { t } = useLanguage();

  if (disabled) return <>{children}</>;

  const items = [
    { key: "new-folder", icon: FolderPlus, label: t.projects.folder.newFolder, action: newFolder },
    ...(newDocument
      ? [
          {
            key: "new-document",
            icon: FileText,
            label: t.documents.newDocument.trigger,
            action: newDocument,
          },
        ]
      : []),
  ];

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-60">
        {items.map(({ key, icon: Icon, label, action }) => (
          <ContextMenuItem
            key={key}
            disabled={action.blockedReason !== undefined}
            onSelect={action.run}
            className="items-start gap-2"
          >
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="flex min-w-0 flex-col">
              <span>{label}</span>
              {action.blockedReason !== undefined && (
                <span className="text-xs text-muted-foreground">{action.blockedReason}</span>
              )}
            </span>
          </ContextMenuItem>
        ))}
      </ContextMenuContent>
    </ContextMenu>
  );
}
